import styles from '@ui/components/Tooltip/Tooltip.module.scss';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useState, useRef } from 'react';
import {
  arrow,
  autoUpdate,
  FloatingArrow,
  FloatingPortal,
  offset,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
  useRole,
} from '@floating-ui/react';

export const Tooltip = ({
  centered = true,
  children,
  isAnimated = false,
  noHover = false,
  percentPosition = 0,
  placement = 'top',
  text,
  isOpen,
  setIsOpen,
}) => {
  const arrowRef = useRef(null);
  const { refs, context, x, y } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen || function () {},
    whileElementsMounted(...args) {
      return autoUpdate(...args, { animationFrame: true });
    },
    placement,
    middleware: [
      offset(8), // multiple of 4px
      shift(),
      arrow({
        element: arrowRef,
      }),
    ],
  });

  const hover = useHover(context, {
    enabled: !noHover,
    delay: {
      open: 0,
      close: 2000,
    },
  });
  const click = useClick(context, {
    enabled: true,
    ignoreMouse: true,
    toggle: true,
  });
  const focus = useFocus(context);
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: 'tooltip' });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    hover,
    click,
    focus,
    dismiss,
    role,
  ]);

  // useEffect is not necessary here, because of autoUpdate
  const position = () => {
    const length = refs?.reference?.current?.clientWidth || 300;
    const resetCenter = x - length / 2;
    return resetCenter + percentPosition * (length / 100);
  };

  return (
    <>
      <div
        className={styles.reference}
        ref={refs.setReference}
        {...getReferenceProps()}
      >
        {children}
      </div>
      <FloatingPortal>
        {isOpen && (
          <div
            className={classNames(styles.tooltip, {
              'big-entrance-animation': isAnimated,
              [styles.animated]: isAnimated,
            })}
            ref={refs.setFloating}
            style={{
              left: centered ? x : position(),
              top: y,
            }}
            {...getFloatingProps()}
          >
            <FloatingArrow
              className={styles.arrow}
              context={context}
              ref={arrowRef}
            />

            <span className={styles.tooltipText}>{text}</span>
          </div>
        )}
      </FloatingPortal>
    </>
  );
};

export const UncontrolledTooltip = props => {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);

  return (
    <Tooltip
      {...props}
      isOpen={uncontrolledOpen}
      setIsOpen={setUncontrolledOpen}
    />
  );
};

Tooltip.propTypes = {
  centered: PropTypes.bool,
  children: PropTypes.node.isRequired,
  isAnimated: PropTypes.bool,
  noHover: PropTypes.bool,
  open: PropTypes.bool,
  percentPosition: PropTypes.number,
  placement: PropTypes.oneOf(['top', 'right', 'bottom', 'left']),
  setOpen: PropTypes.func,
  text: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
};

export default Tooltip;
